import { useCallback, useEffect, useState } from "react";
import { api, errDetail } from "../../lib/api";
import { useApp } from "../../lib/store";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { PermissionMatrix } from "../../components/iam/PermissionMatrix";
import { Copy, Lock, Plus, Trash2, Users } from "lucide-react";

const FLAG_KEYS = ["see_margins", "see_revenue_qty", "see_wallet", "see_visit_notes",
  "see_web_research", "see_ai_analysis", "can_reassign", "require_mfa"];
const SENSITIVE_FLAGS = ["see_margins", "see_revenue_qty", "see_wallet"];

export default function Roles() {
  const { t } = useApp();
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/iam/roles").then((r) => setRoles(r.data || [])).catch((e) => toast.error(errDetail(e)));
  }, []);
  useEffect(() => {
    load();
    api.get("/iam/permission-catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/iam/roles/${editing.id}`, editing);
      toast.success(t("iam_role_saved"));
      setEditing(null); load();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  const createRole = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/iam/roles", creating);
      toast.success(t("iam_role_saved"));
      setCreating(null); load();
      setEditing(data);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  const del = async (r) => {
    try { await api.delete(`/iam/roles/${r.id}`); toast.success("✓"); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="space-y-4" data-testid="iam-roles-page">
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1B2A4A]" style={{ fontFamily: "Work Sans" }}>{t("iam_roles")}</h1>
          <p className="text-xs text-gray-500">{t("iam_roles_sub")}</p>
        </div>
        <Button size="sm" className="ml-auto bg-[#E8871E] hover:bg-[#d1751a] text-white font-semibold rounded-full"
          onClick={() => setCreating({ name: "", clone_from: "", description: "" })} data-testid="new-role-btn">
          <Plus className="h-4 w-4 mr-1" />{t("iam_new_role")}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {roles.map((r) => (
          <div key={r.id} className="rounded-xl border border-gray-200 bg-white p-4 card-hover" data-testid={`role-card-${r.key}`}>
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <p className="font-semibold text-[#1B2A4A] flex items-center gap-1.5">
                  {r.name}
                  {r.system && <Lock className="h-3.5 w-3.5 text-gray-400" title={t("iam_system_role")} />}
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{r.description || "–"}</p>
              </div>
              {!r.system && (
                <button className="text-gray-300 hover:text-red-500" onClick={() => del(r)} data-testid={`role-delete-${r.key}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              <Badge variant="outline" className="text-[10px] flex items-center gap-1">
                <Users className="h-3 w-3" />{r.user_count}
              </Badge>
              <Badge variant="outline" className="text-[10px]">{t("account_visibility")}: {r.account_visibility}</Badge>
              {r.flags?.see_margins && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-inherit text-[10px]">{t("see_margins")}</Badge>}
              {r.flags?.require_mfa && <Badge className="bg-blue-50 text-blue-700 hover:bg-inherit text-[10px]">MFA</Badge>}
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => setEditing(r)} data-testid={`role-open-${r.key}`}>
                {r.system ? t("view") : t("edit")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setCreating({ name: `${r.name} (Kopie)`, clone_from: r.id, description: r.description })}
                data-testid={`role-clone-${r.key}`}><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      {/* create dialog */}
      <Dialog open={!!creating} onOpenChange={(o) => !o && setCreating(null)}>
        <DialogContent className="max-w-md" data-testid="role-create-dialog">
          <DialogHeader>
            <DialogTitle>{t("iam_new_role")}</DialogTitle>
            <DialogDescription>{t("iam_clone_help")}</DialogDescription>
          </DialogHeader>
          {creating && (
            <div className="space-y-3">
              <div className="space-y-1"><Label>{t("name")}</Label>
                <Input value={creating.name} onChange={(e) => setCreating((c) => ({ ...c, name: e.target.value }))}
                  data-testid="role-name-input" /></div>
              <div className="space-y-1"><Label>{t("iam_clone_role")}</Label>
                <Select value={creating.clone_from || "none"} onValueChange={(v) => setCreating((c) => ({ ...c, clone_from: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="role-clone-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">–</SelectItem>
                    {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label>{t("iam_description")}</Label>
                <Textarea rows={2} value={creating.description || ""} onChange={(e) => setCreating((c) => ({ ...c, description: e.target.value }))}
                  data-testid="role-desc-input" /></div>
              <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" disabled={saving || !creating.name}
                onClick={createRole} data-testid="role-create-btn">{t("save")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="role-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editing?.name}
              {editing?.system && <Badge variant="outline" className="text-[10px]">{t("iam_system_role")}</Badge>}
            </DialogTitle>
            <DialogDescription>{editing?.system ? t("iam_role_immutable") : t("iam_matrix_help")}</DialogDescription>
          </DialogHeader>
          {editing && catalog && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1"><Label className="text-xs">{t("name")}</Label>
                  <Input value={editing.name} disabled={editing.system}
                    onChange={(e) => setEditing((r) => ({ ...r, name: e.target.value }))} data-testid="role-edit-name" /></div>
                <div className="space-y-1"><Label className="text-xs">{t("account_visibility")}</Label>
                  <Select value={editing.account_visibility} disabled={editing.system}
                    onValueChange={(v) => setEditing((r) => ({ ...r, account_visibility: v }))}>
                    <SelectTrigger data-testid="role-visibility-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{(catalog.visibility || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className="text-xs">{t("iam_unassigned_view")}</Label>
                  <Select value={editing.unassigned_view} disabled={editing.system}
                    onValueChange={(v) => setEditing((r) => ({ ...r, unassigned_view: v }))}>
                    <SelectTrigger data-testid="role-unassigned-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="basic">{t("unassigned_basic")}</SelectItem>
                      <SelectItem value="hidden">{t("unassigned_hidden")}</SelectItem></SelectContent>
                  </Select></div>
              </div>

              <div className="rounded-lg border border-gray-200 p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {FLAG_KEYS.map((f) => (
                  <label key={f} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5">
                      {t(f)}
                      {SENSITIVE_FLAGS.includes(f) && <span className="text-[10px] text-[#E8871E]">●</span>}
                    </span>
                    <Switch checked={!!editing.flags?.[f]} disabled={editing.system}
                      onCheckedChange={(v) => {
                        if (v && SENSITIVE_FLAGS.includes(f) && !window.confirm(t("iam_sensitive_confirm"))) return;
                        setEditing((r) => ({ ...r, flags: { ...(r.flags || {}), [f]: v } }));
                      }}
                      data-testid={`role-flag-${f}`} />
                  </label>
                ))}
              </div>

              <PermissionMatrix catalog={catalog} matrix={editing.matrix} readOnly={editing.system}
                affectedUsers={editing.user_count || 0}
                onChange={(m) => setEditing((r) => ({ ...r, matrix: m }))} />

              {!editing.system && (
                <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" disabled={saving}
                  onClick={save} data-testid="role-save-btn">{saving ? "…" : t("save")}</Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
